import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import api from "utils/api";
import { toast } from "react-toastify";
import CounselorDetailsTableData from "./data/CounselorDetailsTableData";

function CounselorDetailsManagement() {
  const [counselorDetails, setCounselorDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCounselor, setEditingCounselor] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    image: null,
    phone: "",
    description: "",
  });
  const [imagePreview, setImagePreview] = useState("");

  // Fetch counselor details
  const fetchCounselorDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get("/planwithstudentreview");
      if (response.data.success) {
        // Handle both object and array cases for counselorDetails
        const counselorData = response.data.data?.counselorDetails;
        setCounselorDetails(
          Array.isArray(counselorData) ? counselorData : counselorData ? [counselorData] : []
        );
      }
    } catch (error) {
      console.error("Error fetching counselor details:", error);
      toast.error(error.response?.data?.message || "Failed to fetch counselor details");
      setCounselorDetails([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchCounselorDetails();
  }, []);

  // Handle form input change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle edit counselor
  const handleEditCounselor = (counselor) => {
    setEditingCounselor(counselor);
    setFormData({
      name: counselor.name || "",
      image: null,
      phone: counselor.phone || "",
      description: counselor.description || "",
    });
    setImagePreview(counselor.image || "");
    setDialogOpen(true);
  };

  // Handle save counselor
  const handleSaveCounselor = async () => {
    if (!formData.name || !formData.phone || !formData.description) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("description", formData.description);
      if (formData.image instanceof File) {
        formDataToSend.append("image", formData.image);
      }

      // Update existing counselor
      const response = await api.put(`/planwithstudentreview/counselor`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data.success) {
        toast.success(response.data.message || "Counselor updated successfully!");
        fetchCounselorDetails();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error("Error saving counselor:", error);
      toast.error(error.response?.data?.message || "Failed to save counselor");
    }
  };

  // Filter counselor details based on search (if needed in future)
  const filteredCounselors = counselorDetails;

  const { columns: counselorColumns, rows: counselorRows } = CounselorDetailsTableData(
    filteredCounselors,
    handleEditCounselor
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <ArgonTypography variant="h6">Counselor Details Management</ArgonTypography>
              </ArgonBox>

              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching Counselor Details..
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <ArgonBox>
                  {filteredCounselors.length === 0 ? (
                    <ArgonBox p={3} textAlign="center">
                      <ArgonTypography variant="body2" color="text">
                        No counselor details found.
                      </ArgonTypography>
                    </ArgonBox>
                  ) : (
                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root": {
                          minHeight: "60px", // Increased row height
                        },
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      <Table columns={counselorColumns} rows={counselorRows} />
                    </ArgonBox>
                  )}
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Counselor Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Counselor</DialogTitle>
        <DialogContent>
          <ArgonBox py={2}>
            <ArgonBox mb={2}>
              <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                Name *
              </ArgonTypography>
              <ArgonInput
                placeholder="Enter counselor name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                fullWidth
              />
            </ArgonBox>
            <ArgonBox mb={2}>
              <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                Image Upload
              </ArgonTypography>
              <ArgonBox>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="counselor-image-upload"
                />
                <label htmlFor="counselor-image-upload">
                  <ArgonButton
                    component="span"
                    color="info"
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    Choose Image
                  </ArgonButton>
                </label>
                {imagePreview && (
                  <ArgonBox mt={2} textAlign="center">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                  </ArgonBox>
                )}
              </ArgonBox>
            </ArgonBox>
            <ArgonBox mb={2}>
              <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                Phone *
              </ArgonTypography>
              <ArgonInput
                type="number"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                fullWidth
              />
            </ArgonBox>
            <ArgonBox mb={2}>
              <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                Description *
              </ArgonTypography>
              <ArgonInput
                placeholder="Enter counselor description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                fullWidth
              />
            </ArgonBox>
          </ArgonBox>
        </DialogContent>
        <DialogActions>
          <ArgonButton onClick={() => setDialogOpen(false)}>Cancel</ArgonButton>
          <ArgonButton onClick={handleSaveCounselor} variant="contained" color="primary">
            Save
          </ArgonButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default CounselorDetailsManagement;
